import { ErroreApi } from '../servizi/api.ts'
import { Avviso } from './Pezzi.tsx'

/**
 * Errori dei moduli: quelli trovati dall'interfaccia prima dell'invio e quelli che l'API restituisce.
 *
 * L'API segnala i campi con il loro codice tecnico (`dettagli[].campo`, per esempio
 * `comune_residenza_domicilio` o `ore`) oppure con un codice d'errore sull'intero corpo
 * (`SEDE_MANCANTE`). Prima i moduli li mostravano cosi' com'erano, «ore: Too small…», in testa alla
 * finestra e lontano dal comando appena premuto. Qui ogni errore diventa l'errore del campo a cui si
 * riferisce, con l'etichetta che il campo ha a video; i codici tecnici non si mostrano mai.
 */

export type Dettaglio = { campo: string; messaggio: string }

/** Etichette dei campi di un modulo, per codice del campo nell'API. */
export type Etichette = Record<string, string>

/**
 * Errori per campo ricavati da un errore dell'API. `codiciCampo` porta sul campo gli errori senza
 * dettagli, riconosciuti dal codice (per esempio SEDE_MANCANTE sulla sede).
 */
export function erroriDellApi(e: unknown, codiciCampo: Record<string, string> = {}): Record<string, string> {
  if (!(e instanceof ErroreApi)) return {}
  const errori: Record<string, string> = {}
  for (const d of e.dettagli ?? []) {
    errori[d.campo] = errori[d.campo] ? `${errori[d.campo]} ${d.messaggio}` : d.messaggio
  }
  const campo = e.codice ? codiciCampo[e.codice] : undefined
  if (campo && !errori[campo]) errori[campo] = e.message
  return errori
}

/** Testo di un errore con l'etichetta del campo; senza etichetta nota resta il solo messaggio. */
export function testoErrore(campo: string, messaggio: string, etichette: Etichette): string {
  const etichetta = etichette[campo] ?? etichette[campo.split('.')[0]]
  return etichetta ? `${etichetta}: ${messaggio}` : messaggio
}

/**
 * Avviso d'errore di un modulo, da mettere subito sopra i comandi: con il modulo lungo l'esito
 * dell'invio deve comparire dove si e' appena premuto. Elenca gli errori dei campi con le etichette.
 */
export function AvvisoModulo({ errore, errori = {}, etichette = {} }: {
  errore: string | null; errori?: Record<string, string>; etichette?: Etichette
}) {
  if (!errore) return null
  const voci = Object.entries(errori)
  return (
    <Avviso tipo="errore">
      <strong>{errore}</strong>
      {voci.length > 0 && (
        <ul className="elenco-avviso">
          {voci.map(([campo, messaggio]) => <li key={campo}>{testoErrore(campo, messaggio, etichette)}</li>)}
        </ul>
      )}
    </Avviso>
  )
}

/** Messaggio generale di un errore dell'API: fuori perimetro (404) il testo indicato dal modulo. */
export function messaggioErrore(e: unknown, fuoriPerimetro: string): string {
  if (!(e instanceof ErroreApi)) return 'Non è andata a buon fine: controlla la connessione e riprova.'
  return e.stato === 404 ? fuoriPerimetro : e.message
}
